import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PerformanceCharts } from 'app/modules/shared/classes/performance-chart-options';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import {
    CategoryGroup,
    IChartTitleData,
    ILargeCardInteriorOptions,
} from 'app/modules/shared/interfaces/card-data';
import { NoDataActionType, NoDataTypes } from 'app/modules/shared/interfaces/no-data';
import { BranchService } from 'app/modules/shared/services/branch.service';
import { ToastService } from 'app/modules/shared/services/toast.service';
import * as Highcharts from 'highcharts';
import {
    IProjectionsToActualsLargeCard,
    IProjectionsToActualsTabularListByPosition,
    IProjectionsUser,
    IProjectionsUserParameters,
} from '../../interfaces/performance-data';
import { PerformanceService } from '../../services/performance.service';
import { ProjectionService } from '../../services/projection.service';
import {
    Branch,
    BranchEmpData,
    Year,
} from '../performance-projections-config/performance-projection.data';

@Component({
    selector: 'app-performance-actuals-vs-projections',
    templateUrl: './performance-actuals-vs-projections.component.html',
    styleUrls: ['./performance-actuals-vs-projections.component.scss'],
})
export class PerformanceActualsVsProjectionsComponent implements OnInit {
    branchesOfInterest: Branch[];
    selectedIdentifier: Branch[];
    yearsOfInterest: Year[];
    selectedYear: Year[];
    loading: boolean;
    branchIdentifier: string;
    branchListSettings: any;
    yearOption: string;
    yearListSettings: any;
    employeeListSettings: any;
    employeeList: IProjectionsUser[];
    selectedEmployee: any;
    employeeType: string = 'Loan Originator';
    employeeIdentifierRoute: string;
    employeeIdentifier: string;
    employeeFoundInList: boolean;
    noDataSelection: NoDataTypes;
    parameters: IProjectionsUserParameters = {
        fromDatePeriod: '12m',
        staffingKey: 1,
        ymc: null,
    };
    action: NoDataActionType;
    performanceCharts = new PerformanceCharts.PerformanceCharts(
        this.router,
        this.reusableFunctions
    );
    navFromUrl: string = 'performance/insights/performance-actuals-vs-projections';
    isLevel1User: boolean = false;
    employee: any;
    tableSummaryData: IProjectionsToActualsTabularListByPosition[];
    mainCharts: any[];

    constructor(
        private branchService: BranchService,
        private projectionService: ProjectionService,
        private toast: ToastService,
        private performanceService: PerformanceService,
        private reusableFunctions: ReusableFunctions,
        private router: Router
    ) {
        this.branchListSettings = {
            singleSelection: true,
            text: 'Select Branch',
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'hierarchyName',
            primaryKey: 'hierarchyIdentifier',
            enableFilterSelectAll: false,
        };

        this.yearListSettings = {
            singleSelection: true,
            text: 'Select Year',
            enableSearchFilter: false,
            showCheckbox: false,
            labelKey: 'year',
            primaryKey: 'year',
            enableFilterSelectAll: false,
        };

        this.employeeListSettings = {
            singleSelection: true,
            text: `Select Loan Originator`,
            enableSearchFilter: true,
            showCheckbox: false,
            labelKey: 'employee',
            primaryKey: 'employeeIdentifier',
            enableFilterSelectAll: false,
        };

        this.initializeCharts();
        this.getEmptyLargeCardData();
        this.yearsOfInterest = [];
        this.addYears();
        this.selectedYear = [this.yearsOfInterest[0]];
    }

    ngOnInit(): void {
        this.loading = true;
        this.getBranches();
    }

    initializeCharts() {
        this.mainCharts = [
            {
                name: 'VolumeChart',
            },
            {
                name: 'UnitChart',
            },
        ];
    }

    getEmptyLargeCardData() {
        this.mainCharts.forEach((mainChart) => {
            let chartTitleData: IChartTitleData = {
                showChartCustomTitle: false,
                chartTile: '',
                chartLabelColor: '',
                chartComparisonValue: '',
                chartMainValue: null,
                chartSecondaryValue: '',
                isMainValueUnitVisible: false,
                showMainValue: false,
                chartSubItemValue: '',
                isChartSubValueVisible: false,
                svgIconString: '',
            };

            mainChart.largeCardInteriorOptions = {
                action: this.action,
                componentTitle: '',
                searchTerm: '',
                hasData: false,
                isDataReady: false,
                showTitle: true,
                accessToUrl: '',
                uniqueId: `projections-vs-actuals-${mainChart.name}`,
                chartTitleData: Object.assign({}, chartTitleData),
                noDataCustomMessage: 'Add Projections to see data here.',
            };
        });
    }

    getBranches() {
        this.branchService.getAllRelatedBranches().subscribe(
            (branchEmpData: BranchEmpData) => {
                if (branchEmpData.branches && branchEmpData.branches.length) {
                    this.branchesOfInterest = branchEmpData.branches;
                    this.selectedIdentifier = [
                        this.branchesOfInterest.find((b) => b.hierarchyIdentifier),
                    ];

                    this.setBranch();
                } else if (branchEmpData.employeeData) {
                    this.isLevel1User = true;
                    this.employee = {
                        employee: branchEmpData.employeeData.lfName,
                        employeeIdentifier: branchEmpData.employeeData.primaryIdentifier,
                        hierarchyName: branchEmpData.employeeData.location,
                        hierarchyIdentifier: branchEmpData.employeeData.locationIdentifier,
                    };
                    this.branchesOfInterest = [this.employee];
                    this.selectedIdentifier = [this.employee];
                    this.setBranch();
                } else {
                    this.toast.warning('No branches of interest found for current user.');
                    this.loading = false;
                }
            },
            (error) => {
                console.log(error);
                this.loading = false;
                throw new Error('Problem retrieving the branches.');
            }
        );
    }

    getMainChartData() {
        const parameters = {
            year: Number(this.selectedYear[0].year),
            positionAbbv: 'LO',
            hierarchyIdentifier: this.branchIdentifier,
            employeeIdentifier: this.employeeIdentifier ?? 'All',
            hFilterJSON: '[]',
        };
        this.performanceService.getProjectionsToActualsLargeCard(parameters).subscribe(
            (data: IProjectionsToActualsLargeCard[]) => {
                if (data.length && data.some((x) => x.recordType === 'Projection')) {
                    this.setMainChartData(data, this.mainCharts, true);
                } else {
                    this.setMainChartData([], this.mainCharts, false);
                }
            },
            (error) => {
                console.log('error: ', error);
                this.setMainChartData([], this.mainCharts, false);
            }
        );
    }

    getYears() {
        this.projectionService
            .getYearsOfInterest(this.selectedIdentifier[0].hierarchyIdentifier)
            .subscribe(
                (years) => {
                    this.yearsOfInterest = [];
                    if (years && years.length) {
                        years.forEach((year) => {
                            if (
                                this.yearsOfInterest.findIndex((y) => y.yearValue === year) === -1
                            ) {
                                this.yearsOfInterest.push({
                                    year: year.toString(),
                                    yearValue: year,
                                });
                            }
                        });
                        this.sortYearsData();
                        this.selectedYear = [this.yearsOfInterest[0]];
                    } else {
                        this.yearsOfInterest = [
                            {
                                year: new Date().getFullYear().toString(),
                                yearValue: new Date().getFullYear(),
                            },
                            {
                                year: (new Date().getFullYear() + 1).toString(),
                                yearValue: new Date().getFullYear() + 1,
                            },
                        ];
                        this.selectedYear = [this.yearsOfInterest[0]];
                    }
                    this.getUsersList();
                },
                (error) => {
                    console.log('error: ', error);
                }
            );
    }

    onBranchChange(branch: Branch) {
        if (branch) {
            this.selectedIdentifier = [branch];
            this.selectedEmployee = null;
            this.employeeIdentifier = null;
            this.setBranch();
        }
    }

    setBranch() {
        this.branchIdentifier = this.selectedIdentifier[0].hierarchyIdentifier;
        this.getYears();
    }

    getUsersList() {
        this.parameters.hierarchyIdentifier = this.branchIdentifier;
        if (!this.isLevel1User) {
            this.performanceService.getProjectionsUserList(this.parameters).subscribe((users) => {
                this.employeeList = users;
                this.getMainChartData();
                this.getTableSummaryData();
            });
        } else {
            this.employeeList = [this.employee];
            this.selectedEmployee = [this.employee];
            this.employeeIdentifier = this.employee.employeeIdentifier;
            this.getMainChartData();
            this.getTableSummaryData();
        }
    }

    addYears() {
        const thisYear = new Date().getFullYear();
        if (this.yearsOfInterest.findIndex((y) => y.yearValue === thisYear) === -1) {
            this.yearsOfInterest.push(
                {
                    year: new Date().getFullYear().toString(),
                    yearValue: new Date().getFullYear(),
                },
                {
                    year: (new Date().getFullYear() + 1).toString(),
                    yearValue: new Date().getFullYear() + 1,
                }
            );
        }
        this.sortYearsData();
    }

    sortYearsData() {
        this.yearsOfInterest?.sort((a, b) => a.yearValue - b.yearValue);
    }

    onYearChange(year: Year) {
        if (year) {
            this.selectedYear = [year];
            this.getMainChartData();
            this.getTableSummaryData();
        }
    }

    onEmployeeChange(event) {
        this.employeeIdentifier = this.selectedEmployee[0].employeeIdentifier;
        this.getMainChartData();
        this.getTableSummaryData();
    }

    setMainChartData(data: any[], cardInfo: ILargeCardData[], hasData: boolean): void {
        // Data here contains separate actual / proj data for every lo in every branch. We want to filter out so that our series gets 12 points (1 for each month) at most.
        const filteredData = this.filterDataByYMC(data);
        cardInfo.forEach((card) => {
            const isVolume = card.name === 'VolumeChart';
            let actuals: any = [];
            let projections: any = [];

            filteredData.forEach((record) => {
                let monthShortName = this.reusableFunctions.monthNumToMonthYearArray(
                    record.yearMonthCalendar
                );
                record['datePeriodDescription'] = monthShortName[0];
                const ymc = `${monthShortName[0]} ${monthShortName[1]}`;
                if (record.recordType === 'Actual') {
                    actuals.push({
                        y: isVolume ? record.totalVolume : record.totalUnits,
                        name: monthShortName[0],
                        description: ymc,
                    });
                } else if (record.recordType === 'Projection') {
                    projections.push({
                        y: isVolume ? record.totalVolume : record.totalUnits,
                        name: monthShortName[0],
                        description: ymc,
                    });
                }
            });
            let series: ISeriesData[] = [
                {
                    name: 'Actuals',
                    data: actuals,
                    color: '#0cb090',
                },
                {
                    name: 'Projections',
                    data: projections,
                    color: '#CCCCCC',
                },
            ];

            const subtitle = this.getSubtitle(data, isVolume);

            let categoryGroup: CategoryGroup[] = this.reusableFunctions.getCategories(
                data,
                ['year', 'monthNumber'],
                this.parameters.fromDatePeriod
            )[0];
            let chartOptions = this.performanceCharts.createNewAreaChart(
                isVolume ? 'Dollars ($)' : 'Units #',
                series,
                ['', card.drillDUrlSegment],
                categoryGroup,
                true,
                false,
                subtitle,
                this.navFromUrl,
                isVolume
            );
            this.updateLargeCard(card, chartOptions, hasData);
        });
    }

    updateLargeCard(cardInfo: ILargeCardData, chartOptions: Highcharts.Options, hasData: boolean) {
        if (cardInfo) {
            cardInfo.largeCardInteriorOptions.chart = new Highcharts.Chart(
                cardInfo.largeCardInteriorOptions.uniqueId,
                chartOptions
            );
            cardInfo.largeCardInteriorOptions.hasData = hasData;
            cardInfo.largeCardInteriorOptions.isDataReady = true;
            cardInfo.largeCardInteriorOptions = Object.assign(
                {},
                cardInfo.largeCardInteriorOptions
            );
        }
    }

    getSubtitle(data: IProjectionsToActualsLargeCard[], isVolume: boolean) {
        const actuals = data.find((x) => x.recordType === 'Actual');
        const projections = data.find((x) => x.recordType === 'Projection');

        if (isVolume) {
            const actualVerbiage = actuals
                ? this.reusableFunctions.formatMoney(actuals?.timespanTotalVolume)
                : '$0';
            const projectionsVerbiage = projections
                ? this.reusableFunctions.formatMoney(projections?.timespanTotalVolume)
                : '$0';
            return `${actualVerbiage} | ${projectionsVerbiage}`;
        } else {
            const actualVerbiage = actuals ? actuals?.timespanTotalUnits : '0';
            const projectionsVerbiage = projections ? projections?.timespanTotalUnits : '0';
            return `${actualVerbiage} | ${projectionsVerbiage}`;
        }
    }

    getTableSummaryData() {
        const parameters = {
            year: Number(this.selectedYear[0].year),
            positionAbbv: 'LO',
            hierarchyIdentifier: this.branchIdentifier,
            employeeIdentifier: this.employeeIdentifier ?? 'All',
            hFilterJSON: '[]',
        };
        this.performanceService.getProjectionsToActualsTabularListByPosition(parameters).subscribe(
            (data) => {
                this.tableSummaryData = data;
            },
            (error) => {
                console.log(error);
                this.loading = false;
                this.tableSummaryData = [];
            }
        );
    }

    filterDataByYMC(data) {
        const actualData = [];
        const projData = [];
        data.forEach((d) => {
            if (d.recordType === 'Actual') {
                if (!actualData.some((x) => x.yearMonthCalendar === d.yearMonthCalendar)) {
                    actualData.push(d);
                }
            }
            if (d.recordType === 'Projection') {
                if (!projData.some((x) => x.yearMonthCalendar === d.yearMonthCalendar)) {
                    projData.push(d);
                }
            }
        });
        return [...actualData, ...projData];
    }
}

interface ILargeCardData {
    index: number;
    drillDUrlSegment: string;
    name: string;
    chart: any;
    largeCardInteriorOptions?: ILargeCardInteriorOptions;
}

interface ISeriesData {
    name: string;
    data: Array<any>;
    color: string;
    type?: string;
    marker?: object;
}
